// Cálculo do balancete de verificação (NBC TG 26), puro (sem React).
//
// Extraído do useMemo de Relatorios (App.jsx) sem mudar a regra: a aba
// Balancete e o assistente Fechamento do mês chamam esta mesma função, para que
// os dois mostrem sempre os mesmos números.
//
// @param {object}  p
// @param {Array}   p.lancamentos  lancamentosContabeis (JSON + banco)
// @param {Array}   p.plano        plano de contas da empresa
// @param {string}  p.filAno       'AAAA'
// @param {string}  p.filMes       'MM'
// @param {boolean} p.anual        true = exercício inteiro

// Momento da última alteração de um lançamento do banco (ISO) — usado para saber
// se a conta mudou depois de validada. Lançamento de arquivo JSON devolve null.
export function alteradoEmDoLancamento(l) {
  if (!l || !l._db) return null;
  const a = l._atualizadoEm || null;
  const c = l._criadoEm || null;
  if (a && c) return Date.parse(a) > Date.parse(c) ? a : c;
  return a || c;
}

export function calcularBalancete({ lancamentos, plano, filAno, filMes, anual }) {
  const lancamentosManuais = lancamentos || [];
  const balanceteAnual = !!anual;
  const planoIdx = {};
  (plano || []).forEach(c => { planoIdx[c.codigo] = c; });

  const parseConta = (contaStr, fallbackTipo) => {
    const parts = (contaStr || '').split(/ [—-] /);
    const cod  = (parts[0] || '').trim();
    const nome = parts[1] || contaStr || '';
    const tipo = (planoIdx[cod] || {}).tipo || fallbackTipo || 'D';
    return { cod, nome, tipo };
  };

  const filAnoInt = parseInt(filAno, 10);
  const filMesInt = parseInt(filMes, 10);

  // Função genérica que processa movimentos para um filtro arbitrário de lançamento
  const calcMovs = (filtroFn) => {
    const movs = {};
    const addMov = (key, nome, tipo, deb, cred) => {
      if (!key) return;
      if (!movs[key]) movs[key] = { codigo: key, nome, tipo, debitos: 0, creditos: 0 };
      movs[key].debitos  += deb;
      movs[key].creditos += cred;
    };

    lancamentosManuais.filter(filtroFn).forEach(l => {
      const val = parseFloat(l.valor || 0);
      const d = parseConta(l.contaDebito, 'D');
      const c = parseConta(l.contaCredito, 'R');
      if (d.cod) addMov(d.cod, d.nome, d.tipo, val, 0);
      if (c.cod) addMov(c.cod, c.nome, c.tipo, 0, val);
    });

    return movs;
  };

  // Se há abertura explícita do exercício filtrado, ela JÁ consolida o saldo em 01/01.
  // Nesse caso, ignorar exercícios anteriores (senão duplica saldos patrimoniais).
  const temAbertura = lancamentosManuais.some(x => (x.origem || '') === `abertura-${filAno}`);

  // Movimento do período: no mensal = só o mês; no anual = ano todo, ambos excluindo abertura.
  // Encerramento de exercício (origem `encerramento-{ano}`, datado 31/12) só aparece no balancete
  // ANUAL — no balancete mensal de dezembro mostra-se o pré-encerramento (saldos plenos das contas
  // de resultado). É o padrão Domínio/Questor: o balancete entregue ao cliente em dezembro exibe
  // os saldos do exercício; o encerramento é refletido a partir do exercício seguinte.
  const movsAtual = calcMovs(l => {
    const [ano, mes] = (l.data || '').split('-');
    if (ano !== filAno) return false;
    if (!balanceteAnual && parseInt(mes, 10) !== filMesInt) return false;
    if ((l.origem || '') === `abertura-${filAno}`) return false;
    if (!balanceteAnual && (l.origem || '') === `encerramento-${filAno}`) return false;
    return true;
  });

  // Saldo anterior:
  //  Mensal: exercícios anteriores (se sem abertura) + meses anteriores do mesmo exercício + abertura
  //  Anual:  apenas abertura do exercício filtrado (ou exercícios anteriores se não há abertura)
  const movsAnt = calcMovs(l => {
    const [ano, mes] = (l.data || '').split('-');
    const anoInt = parseInt(ano, 10);
    const mesInt = parseInt(mes, 10);
    const origem = l.origem || '';
    if (anoInt < filAnoInt) return !temAbertura;
    if (anoInt === filAnoInt && origem === `abertura-${filAno}`) return true;
    if (!balanceteAnual && anoInt === filAnoInt && mesInt < filMesInt) return true;
    return false;
  });

  // Calcula saldo respeitando a natureza da conta
  // Ativo (A), Despesa (D), Distribuição (DR): saldo = débitos - créditos (natureza devedora)
  // Passivo (P), PL, Receita (R): saldo = créditos - débitos (natureza credora)
  const calcSaldo = (tipo, deb, cred, codigo) => {
    // Determinar natureza pelo código quando o tipo do plano não é confiável
    const cod = codigo || '';
    // Contas de natureza DEVEDORA (saldo normal = D > C)
    if (tipo === 'A' || tipo === 'D' || tipo === 'DR') return deb - cred;
    if (cod.startsWith('1.')) return deb - cred;      // Ativo
    if (cod.startsWith('3.1.2')) return deb - cred;   // Deduções (despesa)
    if (cod.startsWith('3.3')) return deb - cred;     // Despesas operacionais
    if (cod.startsWith('3.4.1.01')) return deb - cred; // Despesas financeiras
    if (cod.startsWith('5.')) return deb - cred;      // Zeramento
    // Contas de natureza CREDORA (saldo normal = C > D)
    return cred - deb; // P, PL, R, receitas (3.1.1, 3.4.1.02, 4.x)
  };

  // Mesclar: linhas do período atual, com saldoAnterior calculado dos meses anteriores
  const linhas = Object.values(movsAtual).sort((a, b) => a.codigo.localeCompare(b.codigo)).map(l => {
    const ant = movsAnt[l.codigo];
    const saldoAnterior = ant ? calcSaldo(ant.tipo, ant.debitos, ant.creditos, ant.codigo) : 0;
    const saldoFinal = saldoAnterior + calcSaldo(l.tipo, l.debitos, l.creditos, l.codigo);
    return { ...l, saldoAnterior, saldoFinal };
  });

  // Também incluir contas que têm saldo anterior mas sem movimento no período atual
  Object.values(movsAnt).forEach(ant => {
    if (!movsAtual[ant.codigo]) {
      const saldoAnterior = calcSaldo(ant.tipo, ant.debitos, ant.creditos, ant.codigo);
      linhas.push({ ...ant, debitos: 0, creditos: 0, saldoAnterior, saldoFinal: saldoAnterior });
    }
  });
  // Remover contas totalmente zeradas (sem saldo anterior, sem movimento no período e sem saldo final)
  const linhasComMovOuSaldo = linhas.filter(l =>
    Math.abs(l.debitos) > 0.01 ||
    Math.abs(l.creditos) > 0.01 ||
    Math.abs(l.saldoAnterior || 0) > 0.01 ||
    Math.abs(l.saldoFinal || 0) > 0.01
  );
  linhasComMovOuSaldo.sort((a, b) => a.codigo.localeCompare(b.codigo));

  const totalDebitos  = linhasComMovOuSaldo.reduce((s, l) => s + l.debitos,  0);
  const totalCreditos = linhasComMovOuSaldo.reduce((s, l) => s + l.creditos, 0);

  // Indexar lançamentos por conta para drill-down
  const lancamentosPorConta = {};
  const addLanc = (cod, lanc) => {
    if (!cod) return;
    if (!lancamentosPorConta[cod]) lancamentosPorConta[cod] = [];
    lancamentosPorConta[cod].push(lanc);
  };

  // Drill-down: APENAS lançamentos contábeis (manuais + extrato-auto)
  lancamentosManuais.forEach((l, idx) => {
    const [ano, mes] = (l.data || '').split('-');
    const dentroPeriodo = balanceteAnual
      ? (ano === filAno && (l.origem || '') !== `abertura-${filAno}`)
      : (mes === filMes && ano === filAno);
    if (dentroPeriodo) {
      const val = parseFloat(l.valor || 0);
      const d = parseConta(l.contaDebito, 'D');
      const c = parseConta(l.contaCredito, 'R');
      // lancIdx é índice em lancamentosContabeis, mas a edição do drill-down grava em
      // contabilConfig.lancamentos[lancIdx] (outro array — bug antigo, ainda não corrigido).
      // Para lançamento do banco, lancIdx fica nulo: o botão Editar não aparece e nada
      // é gravado no índice errado.
      // `fonte` e `alteradoEm` servem ao assistente de fechamento (conferência por conta).
      const base = l._db
        ? {
          tipo: 'manual', data: l.data, historico: l.historico, valor: val, origem: 'Lançamento do sistema', lancIdx: null, dbId: l._dbId,
          fonte: l.origem === 'extrato' ? 'Extrato bancário' : 'Lançamento no sistema',
          alteradoEm: alteradoEmDoLancamento(l),
        }
        : {
          tipo: 'manual', data: l.data, historico: l.historico, valor: val, origem: 'Manual', lancIdx: idx,
          fonte: 'Arquivo de lançamentos', alteradoEm: null,
        };
      if (d.cod) addLanc(d.cod, { ...base, lado: 'D', conta: d.cod, contrapartida: l.contaCredito || '' });
      if (c.cod) addLanc(c.cod, { ...base, lado: 'C', conta: c.cod, contrapartida: l.contaDebito || '' });
    }
  });

  return { linhas: linhasComMovOuSaldo, totalDebitos, totalCreditos, lancamentosPorConta };
}

export default calcularBalancete;
